import type { DocNode } from '@atlaskit/adf-schema/doc';

import { ConfluencePageAri } from '@atlassian/ari';

import {
	extractSmartLinkSourceExcerptsFromAdf,
	SOURCE_EXCERPT_LIMIT,
} from '../utils/extractSmartLinkSourceExcerptsFromAdf.ts';
import { MAX_RELATED_CONTENT } from '../utils/relatedContentRelationships.ts';
import type { EnhancedSmartLink } from '../utils/smartLinkTypes.ts';

import type {
	AtAGlanceCollection,
	AtAGlanceEntityContext,
	AtAGlanceFeatureInput,
	AtAGlanceLinkedEntity,
	AtAGlanceRelationship,
	AtAGlanceStructuralAncestor,
} from './atAGlanceTypes.ts';
import { extractAdfExcerpt } from './extractAdfExcerpt.ts';

export const MAX_LINKED_ENTITIES = 30;
export const MAX_RELATIONSHIPS_PER_ENTITY = 2;
export const TITLE_LIMIT = 500;
export const ENTITY_SUMMARY_LIMIT = 500;
export const ENTITY_STATUS_LIMIT = 200;
export const PRODUCT_LIMIT = 100;
export const RESOURCE_TYPE_LIMIT = 100;
export const LOCALE_LIMIT = 35;

type BuildAtAGlanceInputOptions = {
	cloudId: string;
	contentId: string;
	locale: string;
	pageTitle: string;
	pageVersion?: number | null;
	pageUpdatedAt?: string | null;
	pageAdf: DocNode;
	structuralAncestors?: readonly AtAGlanceStructuralAncestor[] | null;
	incomingLinks: EnhancedSmartLink[];
	outgoingLinks: EnhancedSmartLink[];
	relatedContentLinks?: EnhancedSmartLink[];
};

const bound = (value: string | undefined, limit: number): string | undefined => {
	const normalized = value?.trim();
	return normalized ? normalized.slice(0, limit) : undefined;
};

const toIsoInstant = (value: string | null | undefined): string | undefined => {
	if (typeof value !== 'string') {
		return undefined;
	}

	const normalized = value.trim();
	if (!normalized || !/(?:Z|[+-]\d{2}:\d{2})$/i.test(normalized)) {
		return undefined;
	}

	const timestamp = new Date(normalized);
	return Number.isNaN(timestamp.getTime()) ? undefined : timestamp.toISOString();
};

const toPositiveContentVersion = (value: number | null | undefined): number | undefined =>
	typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : undefined;

type LinkBackedCollection = Exclude<AtAGlanceCollection, 'ancestors'>;

const relationshipFor = (
	collection: LinkBackedCollection,
	link: EnhancedSmartLink,
	sourceExcerpts: Map<string, string>,
): AtAGlanceRelationship => {
	if (collection === 'appears_in') {
		return {
			collection,
			type: 'content_referenced_entity',
			direction: 'incoming',
			source_excerpt: null,
		};
	}
	if (collection === 'related_content') {
		return {
			collection,
			type: 'confluence_content_has_related_confluence_content',
			direction: 'outgoing',
			source_excerpt: null,
		};
	}
	return {
		collection,
		type: 'page_contains_smart_link',
		direction: 'outgoing',
		source_excerpt: bound(sourceExcerpts.get(link.url), SOURCE_EXCERPT_LIMIT) ?? null,
	};
};

const entityContextFor = (link: EnhancedSmartLink): AtAGlanceEntityContext | undefined => {
	const summary = bound(link.summary, ENTITY_SUMMARY_LIMIT);
	const status =
		bound(link.taskStatus, ENTITY_STATUS_LIMIT) ?? bound(link.state, ENTITY_STATUS_LIMIT);
	const updatedAt = toIsoInstant(link.updatedAt);
	return summary || status || updatedAt
		? {
				...(summary ? { summary } : {}),
				...(status ? { status } : {}),
				...(updatedAt ? { updated_at: updatedAt } : {}),
			}
		: undefined;
};

const mergeEntityContext = (
	primary: AtAGlanceEntityContext | undefined,
	secondary: AtAGlanceEntityContext | undefined,
): AtAGlanceEntityContext | undefined => {
	if (!primary) {
		return secondary;
	}
	if (!secondary) {
		return primary;
	}

	const summary = primary.summary ?? secondary.summary;
	const status = primary.status ?? secondary.status;
	const updatedAt = primary.updated_at ?? secondary.updated_at;
	return {
		...(summary ? { summary } : {}),
		...(status ? { status } : {}),
		...(updatedAt ? { updated_at: updatedAt } : {}),
	};
};

const toEntity = (
	link: EnhancedSmartLink,
	collection: LinkBackedCollection,
	sourceExcerpts: Map<string, string>,
): AtAGlanceLinkedEntity | null => {
	const title = bound(link.title, TITLE_LIMIT);
	if (link.access !== 'granted' || !link.ari || !title) {
		return null;
	}

	const entityContext = entityContextFor(link);
	const product = bound(link.product, PRODUCT_LIMIT);
	const resourceType = bound(link.resourceType, RESOURCE_TYPE_LIMIT);

	return {
		ari: link.ari,
		title,
		...(product ? { product } : {}),
		...(resourceType ? { resource_type: resourceType } : {}),
		relationships: [relationshipFor(collection, link, sourceExcerpts)],
		...(entityContext ? { entity_context: entityContext } : {}),
	};
};

const collectEntities = (
	links: EnhancedSmartLink[],
	collection: LinkBackedCollection,
	sourceExcerpts: Map<string, string>,
): AtAGlanceLinkedEntity[] => {
	const entities: AtAGlanceLinkedEntity[] = [];
	for (const link of links) {
		const entity = toEntity(link, collection, sourceExcerpts);
		if (entity) {
			entities.push(entity);
		}
	}
	return entities;
};

const structuralParentEntityFor = (
	cloudId: string,
	ancestors: readonly AtAGlanceStructuralAncestor[] | null | undefined,
): AtAGlanceLinkedEntity | null => {
	if (!ancestors) {
		return null;
	}

	// Confluence ancestors are root-first. Select one nearest page ancestor so
	// hierarchy evidence stays useful and bounded even below folders/databases.
	for (let index = ancestors.length - 1; index >= 0; index--) {
		const ancestor = ancestors[index];
		const id = ancestor?.id?.trim();
		const title = bound(ancestor?.title ?? undefined, TITLE_LIMIT);
		if (!id || !title || ancestor?.type?.toLowerCase() !== 'page') {
			continue;
		}

		return {
			ari: ConfluencePageAri.create({ siteId: cloudId, pageId: id }).toString(),
			title,
			product: 'confluence',
			resource_type: 'page',
			relationships: [
				{
					collection: 'ancestors',
					type: 'confluence_page_has_child_confluence_page',
					direction: 'incoming',
					source_excerpt: null,
				},
			],
		};
	}

	return null;
};

const prioritizeRelationships = (
	relationships: AtAGlanceRelationship[],
): AtAGlanceRelationship[] => {
	const linksWithExcerpt: AtAGlanceRelationship[] = [];
	const linksWithoutExcerpt: AtAGlanceRelationship[] = [];
	const relatedContent: AtAGlanceRelationship[] = [];
	const ancestors: AtAGlanceRelationship[] = [];
	const remaining: AtAGlanceRelationship[] = [];

	for (const relationship of relationships) {
		if (relationship.collection === 'links_to') {
			(relationship.source_excerpt ? linksWithExcerpt : linksWithoutExcerpt).push(relationship);
		} else if (relationship.collection === 'related_content') {
			relatedContent.push(relationship);
		} else if (relationship.collection === 'ancestors') {
			ancestors.push(relationship);
		} else {
			remaining.push(relationship);
		}
	}

	return [
		...linksWithExcerpt,
		...linksWithoutExcerpt,
		...relatedContent,
		...ancestors,
		...remaining,
	].slice(0, MAX_RELATIONSHIPS_PER_ENTITY);
};

const mergeByAri = (entities: AtAGlanceLinkedEntity[]): AtAGlanceLinkedEntity[] => {
	const byAri = new Map<string, AtAGlanceLinkedEntity>();

	for (const entity of entities) {
		const existing = byAri.get(entity.ari);
		const nextRelationship = entity.relationships[0];
		if (!existing) {
			byAri.set(entity.ari, entity);
			continue;
		}
		if (!nextRelationship) {
			continue;
		}

		const existingRelationshipIndex = existing.relationships.findIndex(
			(relationship) => relationship.collection === nextRelationship.collection,
		);
		if (existingRelationshipIndex !== -1) {
			const existingRelationship = existing.relationships[existingRelationshipIndex];
			if (!existingRelationship) {
				continue;
			}
			const entityContext = mergeEntityContext(existing.entity_context, entity.entity_context);
			const relationship =
				!existingRelationship.source_excerpt && nextRelationship.source_excerpt
					? { ...existingRelationship, source_excerpt: nextRelationship.source_excerpt }
					: existingRelationship;
			const relationships = [...existing.relationships];
			relationships[existingRelationshipIndex] = relationship;
			byAri.set(entity.ari, {
				...existing,
				product: existing.product ?? entity.product,
				resource_type: existing.resource_type ?? entity.resource_type,
				relationships,
				...(entityContext ? { entity_context: entityContext } : {}),
			});
			continue;
		}

		const entityContext = mergeEntityContext(existing.entity_context, entity.entity_context);
		byAri.set(entity.ari, {
			...existing,
			product: existing.product ?? entity.product,
			resource_type: existing.resource_type ?? entity.resource_type,
			relationships: [...existing.relationships, nextRelationship],
			...(entityContext ? { entity_context: entityContext } : {}),
		});
	}

	return [...byAri.values()].map((entity) => ({
		...entity,
		relationships: prioritizeRelationships(entity.relationships),
	}));
};

const hasCollection = (entity: AtAGlanceLinkedEntity, collection: AtAGlanceCollection): boolean =>
	entity.relationships.some((relationship) => relationship.collection === collection);

const prioritizeAndCapEntities = (
	entities: AtAGlanceLinkedEntity[],
	incomingLinks: EnhancedSmartLink[],
	outgoingLinks: EnhancedSmartLink[],
	relatedContentLinks: EnhancedSmartLink[],
): AtAGlanceLinkedEntity[] => {
	const mergedOrder = new Map(entities.map((entity, index) => [entity.ari, index]));
	const incomingOrder = new Map<string, number>();
	const outgoingOrder = new Map<string, number>();
	const relatedOrder = new Map<string, number>();
	const outgoingWorkItemAris = new Set<string>();

	incomingLinks.forEach((link, index) => {
		if (link.ari && !incomingOrder.has(link.ari)) {
			incomingOrder.set(link.ari, index);
		}
	});
	outgoingLinks.forEach((link, index) => {
		if (!link.ari) {
			return;
		}
		const documentOrder = Number.isFinite(link.occurrenceIndex) ? link.occurrenceIndex : index;
		const currentOrder = outgoingOrder.get(link.ari);
		if (currentOrder === undefined || documentOrder < currentOrder) {
			outgoingOrder.set(link.ari, documentOrder);
		}
		if (link.isTask) {
			outgoingWorkItemAris.add(link.ari);
		}
	});
	relatedContentLinks.forEach((link, index) => {
		if (link.ari && !relatedOrder.has(link.ari)) {
			relatedOrder.set(link.ari, index);
		}
	});

	const sortBy =
		(order: Map<string, number>) =>
		(left: AtAGlanceLinkedEntity, right: AtAGlanceLinkedEntity): number =>
			(order.get(left.ari) ?? Number.MAX_SAFE_INTEGER) -
				(order.get(right.ari) ?? Number.MAX_SAFE_INTEGER) ||
			(mergedOrder.get(left.ari) ?? Number.MAX_SAFE_INTEGER) -
				(mergedOrder.get(right.ari) ?? Number.MAX_SAFE_INTEGER);

	// Related-content one-liners are the only summaries the UI renders, and
	// cc-smarts keeps 12 candidates with input-index as the tie-break. Keep
	// those ARIs at the front of the 30 so they are not dropped behind bulk
	// appears-in or links-to.
	const reservedRelated = entities
		.filter((entity) => hasCollection(entity, 'related_content'))
		.sort(sortBy(relatedOrder))
		.slice(0, MAX_RELATED_CONTENT);
	const reservedRelatedAris = new Set(reservedRelated.map((entity) => entity.ari));
	const afterRelated = entities.filter((entity) => !reservedRelatedAris.has(entity.ari));

	// A parent that is also explicitly linked belongs to the stronger outgoing
	// tier and must not consume an additional reserved ancestor slot.
	const reservedAncestor = afterRelated.find(
		(entity) => hasCollection(entity, 'ancestors') && !hasCollection(entity, 'links_to'),
	);
	const remaining = afterRelated.filter((entity) => entity !== reservedAncestor);
	const outgoingWorkItems = remaining
		.filter((entity) => hasCollection(entity, 'links_to') && outgoingWorkItemAris.has(entity.ari))
		.sort(sortBy(outgoingOrder));
	const otherOutgoing = remaining
		.filter((entity) => hasCollection(entity, 'links_to') && !outgoingWorkItemAris.has(entity.ari))
		.sort(sortBy(outgoingOrder));
	const incomingOnly = remaining
		.filter((entity) => !hasCollection(entity, 'links_to'))
		.sort(sortBy(incomingOrder));
	const prioritized = [...outgoingWorkItems, ...otherOutgoing, ...incomingOnly];
	const reservedPrefix = [...reservedRelated, ...(reservedAncestor ? [reservedAncestor] : [])];

	return [
		...reservedPrefix,
		...prioritized.slice(0, Math.max(0, MAX_LINKED_ENTITIES - reservedPrefix.length)),
	];
};

/**
 * Builds the cc-smarts `twg-hub-summaries` request from the modal's connection
 * data and the current page ADF. Returns null when there is nothing useful to
 * send (no page text, or no access-granted linked entities with ARIs).
 */
export const buildAtAGlanceInput = ({
	cloudId,
	contentId,
	locale,
	pageTitle,
	pageVersion,
	pageUpdatedAt,
	pageAdf,
	structuralAncestors,
	incomingLinks,
	outgoingLinks,
	relatedContentLinks = [],
}: BuildAtAGlanceInputOptions): AtAGlanceFeatureInput | null => {
	const normalizedLocale = locale.trim();
	const title = bound(pageTitle, TITLE_LIMIT);
	if (!normalizedLocale || normalizedLocale.length > LOCALE_LIMIT || !title) {
		return null;
	}

	const { excerpt, truncated } = extractAdfExcerpt(pageAdf);
	if (!excerpt) {
		return null;
	}

	const sourceExcerpts =
		outgoingLinks.length > 0
			? extractSmartLinkSourceExcerptsFromAdf(pageAdf)
			: new Map<string, string>();
	const incoming = collectEntities(incomingLinks, 'appears_in', sourceExcerpts);
	const outgoing = collectEntities(outgoingLinks, 'links_to', sourceExcerpts);
	const related = collectEntities(relatedContentLinks, 'related_content', sourceExcerpts);
	const structuralParent = structuralParentEntityFor(cloudId, structuralAncestors);
	const linkedEntities = prioritizeAndCapEntities(
		mergeByAri([
			...(structuralParent ? [structuralParent] : []),
			...related,
			...incoming,
			...outgoing,
		]),
		incomingLinks,
		outgoingLinks,
		relatedContentLinks,
	);
	if (linkedEntities.length === 0) {
		return null;
	}

	const version = toPositiveContentVersion(pageVersion);
	const updatedAt = toIsoInstant(pageUpdatedAt);

	return {
		content_ari: ConfluencePageAri.create({ siteId: cloudId, pageId: contentId }).toString(),
		locale: normalizedLocale,
		central_page: {
			title,
			mime_type: 'text/markdown',
			excerpt,
			truncated,
			...(version ? { version } : {}),
			...(updatedAt ? { updated_at: updatedAt } : {}),
		},
		linked_entities: linkedEntities,
	};
};
